'use client';

import userIcon from '@/assets/images/UserWhite.png';
import { getUserProfilePicture } from '@/domain/user/utils';
import { useAuth } from '@/hooks/useAuth';
import useQueryParams from '@/hooks/useQueryParams';
import { Button } from '@andritzot/metris-web-ui/inputs/button';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { Menu } from '@andritzot/metris-web-ui/navigation/menu';
import { signIn, useSession } from 'next-auth/react';
import Image from 'next/image';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import UserMenuPopup from './UserMenuPopup';
import { ViewProfile } from './ViewProfile';

interface RightContainerProps {
  isInternal: boolean;
  isLoggedIn: boolean;
}

const RightContainer = ({ isInternal, isLoggedIn }: RightContainerProps) => {
  const { status, update } = useSession();
  const { user } = useAuth();
  const { queryParams, setQueryParams } = useQueryParams();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [showUser, setShowUser] = useState(false);
  const [viewProfile, setViewProfile] = useState(false);

  useEffect(() => {
    if (queryParams.has('error')) {
      toast.error(queryParams.get('error') ?? 'Unknown error', { autoClose: false, toastId: 'login-error' });
      setQueryParams({ error: null });
    }
  }, [queryParams, setQueryParams]);

  const updateRef = useRef(update);
  useEffect(() => {
    updateRef.current = update;
  }, [update]);

  useEffect(() => {
    if (!isLoggedIn || status !== 'unauthenticated') return;
    const timeoutId = setTimeout(() => {
      void updateRef.current();
    }, 3000);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [isLoggedIn, status]);

  const onClickMenu = useCallback(() => {
    setShowUser((prev) => !prev);
    setViewProfile(false);
  }, []);

  const openViewProfile = useCallback(() => {
    setViewProfile(true);
    setShowUser(false);
  }, []);

  const handleCloseUserDialog = useCallback(() => {
    setShowUser(false);
  }, []);

  const handleCloseViewProfile = useCallback(() => {
    setViewProfile(false);
  }, []);

  const handleLogin = useCallback(async () => {
    await signIn('keycloak');
  }, []);

  return isInternal ? (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        ml: 'auto',
        mr: { xs: '1rem', sm: 0 },
        '& a': { textDecoration: 'none' },
      }}
    >
      {isLoggedIn ? (
        <>
          <Box
            component="button"
            tabIndex={0}
            type="button"
            onKeyDown={onClickMenu}
            onClick={onClickMenu}
            sx={{ background: 'transparent', border: 0, cursor: 'pointer', outline: 0, p: 0, m: 0 }}
          >
            <Box
              ref={setAnchorEl}
              sx={{
                display: 'inline-block',
                flexShrink: 0,
                position: 'relative',
                borderRadius: '0.475rem',
                ml: '15px',
                cursor: 'pointer',
                '& img': { width: '2rem', height: '2rem', borderRadius: '50%' },
              }}
            >
              {user?.pictureGuid ? (
                <img src={getUserProfilePicture(user.pictureGuid)} width={28} height={28} alt="user" />
              ) : (
                <Image src={userIcon} width={28} height={28} alt="user" />
              )}
            </Box>
          </Box>
          <Menu
            anchorEl={anchorEl}
            open={showUser}
            onClose={handleCloseUserDialog}
            disableScrollLock
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{ paper: { sx: { width: 300, mt: 1 } }, list: { sx: { py: 0 } } }}
          >
            <UserMenuPopup openViewProfile={openViewProfile} handleClose={handleCloseUserDialog} />
          </Menu>
          <ViewProfile visibleProfile={viewProfile} handleCloseProfile={handleCloseViewProfile} />
        </>
      ) : (
        <Button variant="text" onClick={handleLogin}>
          <Box
            component="span"
            sx={{
              fontWeight: 600,
              letterSpacing: '0.08em',
              color: '#fff',
              display: 'block',
              fontSize: '14px',
              textTransform: 'uppercase',
              px: '1px',
            }}
          >
            LOGIN
          </Box>
        </Button>
      )}
    </Box>
  ) : null;
};

export default RightContainer;
